import type { OverlayParams } from '../../../shared/lib/webcodecs/types';
import { TEMPLATE_SIZE } from '../../../shared/lib/video';

export interface BuildReelOverlayInput {
  text: string;
  lines: string[];
  /** Столько первых строк покрашены целиком первым цветом. */
  primaryLines?: number;
  /** Сколько символов строки primaryLines ещё покрашены первым цветом:
   *  граница проходит посреди строки, до двоеточия. */
  splitChars?: number;
  fontSize: number;        // preview px (как wrapResult.fontSize)
  posX: number;            // template-space px (textPosition.x)
  posBottom: number;       // template-space px (textPosition.bottom)
  containerScale: number;  // rect.width / TEMPLATE_SIZE.width
  fontFamily?: string;
  fontUrl?: string;
  textUppercase?: boolean;
  fontVariationSettings?: Record<string, number>;
  backgroundColor?: string;
  backgroundAlpha?: number;
  textColor?: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  logoPosition?: OverlayParams['logoPosition'];
  logoSize?: number;
  lineSpacing?: number;
  textAlign?: 'left' | 'center' | 'right';
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  hCenterX?: number; // fraction 0..1; when set, overlay is center-anchored
}

/**
 * Строит OverlayParams для превью ровно как onTextPositionChange + buildRenderTasks
 * передают серверу: x в css-px (template px × containerScale), bottom в template-px,
 * previewWidth в css-px. Тогда drawOverlay на 1080×1920 canvas даёт тот же кадр,
 * что серверный бёрн (та же функция, тот же Canvas2D).
 */
export function buildReelOverlayParams(input: BuildReelOverlayInput): OverlayParams {
  const previewWidth = input.containerScale * TEMPLATE_SIZE.width;
  const previewHeight = input.containerScale * TEMPLATE_SIZE.height;
  return {
    text: input.text,
    logoArrayBuffer: null,
    lines: input.lines,
    primaryLines: input.primaryLines,
    splitChars: input.splitChars,
    textPosition: {
      x: input.posX * input.containerScale,
      y: 0,
      previewWidth,
      previewHeight,
      textBoxWidth: 0,
      bottom: input.posBottom,
    },
    fontSize: input.fontSize,
    lineSpacing: input.lineSpacing,
    fontFamily: input.fontFamily || 'Arial',
    fontUrl: input.fontUrl,
    textUppercase: !!input.textUppercase,
    fontVariationSettings: input.fontVariationSettings,
    backgroundColor: input.backgroundColor || '#000000',
    backgroundAlpha: input.backgroundAlpha,
    textColor: input.textColor || '#ffffff',
    useTwoColors: input.useTwoColors,
    textColorSecondary: input.textColorSecondary,
    textShadowEnabled: input.textShadowEnabled,
    textShadowColor: input.textShadowColor,
    textShadowBlur: input.textShadowBlur,
    textShadowWidth: input.textShadowWidth,
    textSoftShadowEnabled: input.textSoftShadowEnabled,
    textSoftShadowColor: input.textSoftShadowColor,
    textSoftShadowBlur: input.textSoftShadowBlur,
    textSoftShadowOffsetX: input.textSoftShadowOffsetX,
    textSoftShadowOffsetY: input.textSoftShadowOffsetY,
    textSoftShadowOpacity: input.textSoftShadowOpacity,
    textShadowOpacity: input.textShadowOpacity,
    logoPosition: input.logoPosition,
    logoSize: input.logoSize,
    textAlign: input.textAlign,
    textBoxStyle: input.textBoxStyle,
    textBoxAngle: input.textBoxAngle,
    textBoxRadius: input.textBoxRadius,
    textBoxPadding: input.textBoxPadding,
    textBoxPaddingTop: input.textBoxPaddingTop,
    textBoxPaddingLeft: input.textBoxPaddingLeft,
    textBoxPaddingRight: input.textBoxPaddingRight,
    hCenterX: input.hCenterX,
    textDuration: 'full',
  };
}
